import { useState } from "react";
import { TertiaryToggleButtonNodeTitle } from "@foodpilot/foods";
import { Stack, CircularProgress, useTheme } from "@mui/material";
import { HintBox } from "src/components/HintBox";
import { ScoreService } from "src/services/ScoreService";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { NoScoresAvailableMessage } from "src/components/Message/NoScoresAvailableMessage";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { ScoreToggleButton } from "./ScoreToggleButton";
import {
    OlapScores,
    OlapScoresWithPrevScores,
    resolveRowSourceMeta,
} from "src/api/client-api/olapScores/olapScoresSchema";
import { SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { resolveRowContributors } from "src/api/client-api/olapScores/olapScoresSchema";
import { ScoreScatterPlot, VisualisationChoice } from "./ScoreScatterPlot/ScoreScatterPlot";
import { Product } from "src/api/client-api/products/useGetOneProduct";
import { Ingredient } from "src/api/client-api/ingredients/useGetOneIngredient";
import { PostList } from "src/api/client-api/post/types/postList.types";
import { ProductScoreOverviewCards } from "./ProductScoreOverviewCards";
import { ProductScoreOverviewDetails } from "./ProductScoreOverviewDetails";
import { isDisplayedAsMainScore } from "src/utils/scores";

export type ScoresScatterPlotProps<T extends VisualisationChoice> = {
    entity: Product | Ingredient;

    scores: OlapScoresWithPrevScores | undefined;
    areScoresRefetching: boolean;

    scatteredScores: OlapScores | undefined;
    areScatteredScoresRefetching: boolean;
    scatteredScoresDimension: string;
    getActivityDataValueByCompanyId: (companyId: number, visualisationChoiceKey: T["key"]) => number;

    ssqVersion: SsqVersion | undefined;
    isSsqVersionReady: boolean;
    posts: PostList[] | undefined;

    visualisationChoices: T[];
    textOptions: {
        dataLabel: string;
        noScoresTitle: string;
        noScoresContent: string;
    };

    onScoreDistributionClick?: (scoreId: number) => void;
    referenceScores?: Record<number, number>;
    atLeastOnePostLocked?: boolean;
    overviewVariant?: "legacy" | "product-v2";
};
export const ScoresScatterPlot = <T extends VisualisationChoice>(props: ScoresScatterPlotProps<T>) => {
    const {
        entity,
        scores,
        areScoresRefetching,
        scatteredScores,
        areScatteredScoresRefetching,
        scatteredScoresDimension,
        getActivityDataValueByCompanyId,
        ssqVersion,
        isSsqVersionReady,
        posts,
        visualisationChoices,
        textOptions,
        onScoreDistributionClick,
        referenceScores,
        atLeastOnePostLocked,
        overviewVariant = "legacy",
    } = props;

    const theme = useTheme();
    const [selectedScoreId, setSelectedScoreId] = useState<number | null>(null);

    const {
        entityScores,
        scoresWithVariations,
        allScoresWithVariations,
        selectedVariations,
        lastSelectedVariation,
        selectVariation,
    } = useEntityScores();

    const ssq = ssqVersion?.ssq;
    const usesCompositionScores = overviewVariant === "product-v2" && ssq?.monolithic === false;
    const availableScoresWithVariations = usesCompositionScores ? allScoresWithVariations : scoresWithVariations;
    const mainScores = availableScoresWithVariations.filter(
        (scoreWithVariation) => scoreWithVariation.variationId === null,
    );
    const ssqScoreIds = ssq ? ScoreService.getScoreIdsFromSsq(ssq) : [];
    const configuredMainChildScoreIds = new Set(entityScores.filter(isDisplayedAsMainScore).map(({ id }) => id));
    const displayedMainScores = mainScores.filter(
        ({ scoreId }) =>
            ssqScoreIds.length === 0 ||
            ssqScoreIds.includes(scoreId) ||
            (usesCompositionScores && configuredMainChildScoreIds.has(scoreId)),
    );
    const selectableMainScores = displayedMainScores;
    const selectedMainScore =
        selectableMainScores.find(({ scoreId }) => scoreId === selectedScoreId) ?? selectableMainScores[0] ?? null;
    const selectedScoreIndex = Math.max(
        0,
        displayedMainScores.findIndex(({ scoreId }) => scoreId === selectedMainScore?.scoreId),
    );
    const selectedVariationId = selectedMainScore ? selectedVariations.query?.[selectedMainScore.scoreId] : undefined;
    const selectedScoreVariation =
        selectedMainScore ?
            (availableScoresWithVariations.find(
                ({ scoreId, variationId }) =>
                    scoreId === selectedMainScore.scoreId && variationId === selectedVariationId,
            ) ??
            availableScoresWithVariations.find(
                ({ scoreId, defaultDisplay }) => scoreId === selectedMainScore.scoreId && defaultDisplay,
            ) ??
            selectedMainScore)
        :   null;
    const scoreWithVariation = overviewVariant === "product-v2" ? selectedScoreVariation : selectedMainScore;

    // The OLAP endpoint returns a sidecar `scoreSourceMeta` payload describing
    // the cascade outcome per supplier and for the global fallback (cf. backend
    // `IngredientScoreSourceMetaResolver`). The scatter view shows the aggregate
    // ingredient score, so we enumerate the *distinct* contributors: the chip
    // composes a multi-source tooltip (e.g. "Score basé sur la référence
    // fournisseur Valrhona et la référence plateforme.") and falls back to a
    // single-source wording when only one contributor remains. Pure-SSQ rows
    // produce an empty contributors list → no chip (precise value path).

    // Enumerate the distinct cascade contributors so the chip tooltip can spell
    // every one of them out (e.g. "Score basé sur la référence fournisseur
    // Valrhona et la référence plateforme."). The chip itself filters pure-SSQ
    // rows away, so when every supplier sends precise questionnaire data, no
    // badge is rendered.
    const aggregateContributors = resolveRowContributors(scores?.scoreSourceMeta);

    // Kept for the inherited-from-reference tooltip below, which still needs a
    // single descriptor (a sentence rather than a chip). The single-source
    // helper mirrors the legacy pre-cascade behaviour.
    const aggregateSourceMeta = resolveRowSourceMeta(scores?.scoreSourceMeta);

    // Detect whether the displayed value was inherited from the platform reference because no
    // SSQ is filled for this ingredient. See ingredient-reference-scores-fallback plan (fe-7).
    const ingredientReferenceScores = entity?.jsonAttributes?.referenceScores as Record<string, number> | undefined;
    const referenceScoreForCurrent =
        scoreWithVariation ? ingredientReferenceScores?.[scoreWithVariation.scoreId] : undefined;

    const score = scoreWithVariation ? scores?.olapScores[scoreWithVariation.scoreId] : undefined;
    const scoreValue = OlapScoresService.normalizeNumericalScore(score?.score.value);

    const isInheritedFromReference =
        (("ingredientReferenceId" in entity && entity?.ingredientReferenceId !== undefined) ||
            ("productReferenceId" in entity && entity?.productReferenceId !== undefined)) &&
        (posts === undefined || posts.length === 0) &&
        referenceScoreForCurrent !== undefined &&
        scoreValue !== null &&
        Math.abs(scoreValue - referenceScoreForCurrent) < 1e-6;

    const scoresChoices = (() => {
        if (!scores) {
            return null;
        }

        return displayedMainScores.map((score, index) => {
            const totalScore =
                OlapScoresService.normalizeNumericalScore(scores.olapScores[score.scoreId]?.score.value) ?? undefined;

            const selectedVariationId = selectedVariations.query?.[score.scoreId] ?? null;
            const selectedVariation =
                availableScoresWithVariations.find(
                    (scoreWithVariation) =>
                        scoreWithVariation.scoreId === score.scoreId &&
                        scoreWithVariation.variationId === selectedVariationId,
                ) ?? availableScoresWithVariations[0];

            return (
                <ScoreToggleButton
                    key={index}
                    label={score.group}
                    unit={selectedVariation.unit}
                    precision={selectedVariation.precision}
                    value={totalScore}
                    isRefetching={areScoresRefetching && lastSelectedVariation.scoreId === score.scoreId}
                    contributors={aggregateContributors}
                />
            );
        });
    })();

    if (!scoreWithVariation) {
        return <NoScoresAvailableMessage appearance={overviewVariant} />;
    }

    const scatterContentProps = {
        scores,
        areScoresRefetching: areScoresRefetching || !isSsqVersionReady,
        scatteredScores,
        areScatteredScoresRefetching: areScatteredScoresRefetching || !isSsqVersionReady,
        scatteredScoresDimension,
        getActivityDataValueByCompanyId,
        scoreWithVariation,
        visualisationChoices,
        dataLabel: textOptions.dataLabel,
        onScoreDistributionClick,
        referenceScores,
        atLeastOnePostLocked,
        referenceEntityData: {
            aggregateContributors,
            aggregateSourceMeta,
            isInheritedFromReference,
        },
    };

    return (
        <Stack gap={theme.spacing(3.5)}>
            {ssq && ssqScoreIds.length === 0 && (
                <HintBox title={textOptions.noScoresTitle} content={textOptions.noScoresContent} />
            )}

            <Stack
                data-testid="product-score-overview-composition"
                gap={overviewVariant === "product-v2" ? theme.spacing(0) : theme.spacing(3.5)}
            >
                {overviewVariant === "product-v2" && isSsqVersionReady ?
                    <ProductScoreOverviewCards
                        areScoresRefetching={areScoresRefetching}
                        entityScores={entityScores}
                        lastSelectedVariation={lastSelectedVariation}
                        mainScores={displayedMainScores}
                        onScoreSelect={(scoreId, variationId) => {
                            setSelectedScoreId(scoreId);
                            selectVariation(scoreId, variationId);
                        }}
                        onVariationSelect={selectVariation}
                        scores={scores}
                        scoresWithVariations={availableScoresWithVariations}
                        selectedScoreId={scoreWithVariation.scoreId}
                        selectedVariationIds={selectedVariations.query}
                    />
                : scoresChoices !== null && isSsqVersionReady ?
                    <TertiaryToggleButtonNodeTitle
                        choices={scoresChoices}
                        currentIndexSelected={selectedScoreIndex}
                        onClick={(index) => setSelectedScoreId(displayedMainScores[index]?.scoreId ?? null)}
                    />
                :   <CircularProgress size={26} sx={{ my: 2 }} />}

                {overviewVariant === "product-v2" ?
                    <ProductScoreOverviewDetails
                        {...scatterContentProps}
                        isFirstScoreSelected={displayedMainScores[0]?.scoreId === scoreWithVariation.scoreId}
                    />
                :   <ScoreScatterPlot {...scatterContentProps} />}
            </Stack>
        </Stack>
    );
};
